import React, { useContext, useEffect, useRef, useState } from 'react';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { Button } from 'baseui/button';
import { Spinner } from 'baseui/spinner';
import AuthContext from './shared/AuthContext';
import AuthShell from './auth/AuthShell';
import PageMeta from './seo/PageMeta';
import { getOrCreateDeviceId } from '../utils/deviceId';

const Signup = ({ onBackToLogin }) => {
  const { signUp } = useContext(AuthContext);
  const [formData, setFormData] = useState({ fullName: '', email: '', password: '' });
  const [errors, setErrors] = useState({ fullName: '', email: '', password: '', api: '' });
  const [isLoading, setIsLoading] = useState(false);
  const fullNameInputRef = useRef(null);

  useEffect(() => {
    fullNameInputRef.current?.focus();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '', api: '' }));
  };

  const validateForm = () => {
    const newErrors = { fullName: '', email: '', password: '', api: '' };
    let isValid = true;
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
      isValid = false;
    } else if (formData.fullName.trim().length < 3) {
      newErrors.fullName = 'Name must be at least 3 characters';
      isValid = false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      newErrors.email = 'Valid email is required';
      isValid = false;
    }
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(formData.password)) {
      newErrors.password = '8+ chars with upper, lower, number & special (@$!%*?&)';
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    const deviceId = getOrCreateDeviceId();
    try {
      const result = await signUp({
        ...formData,
        roles: ['ROLE_USER'],
        restrictions: [''],
        deviceId,
      });
      if (result?.success === false) {
        setErrors((prev) => ({ ...prev, api: result?.error || 'Signup failed.' }));
      }
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        api: err.response?.data?.message || 'Signup failed.',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <PageMeta
        title="Create account"
        description="Join QnaRead free — structured Q&A notebooks, library search, spaced repetition, and Google sign-in."
      />
      <AuthShell
        title="Create your account"
        subtitle="Free plan includes search, practice, review, and daily AI quota"
        footer={
          <p className="auth-card-footer">
            Already have an account?{' '}
            <button type="button" className="linkish" onClick={() => onBackToLogin?.()}>
              Sign in
            </button>
          </p>
        }
      >
        {errors.api && <div className="auth-error" role="alert">{errors.api}</div>}
        <form onSubmit={handleSignup}>
          <FormControl label="Full name" error={errors.fullName}>
            <Input
              inputRef={fullNameInputRef}
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Your name"
            />
          </FormControl>
          <div className="auth-field">
            <FormControl label="Email" error={errors.email}>
              <Input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="you@example.com" />
            </FormControl>
          </div>
          <div className="auth-field">
            <FormControl label="Password" error={errors.password}>
              <Input name="password" type="password" value={formData.password} onChange={handleChange} placeholder="Create a strong password" />
            </FormControl>
          </div>
          <Button type="submit" isLoading={isLoading} overrides={{ BaseButton: { style: { width: '100%' } } }}>
            {isLoading ? <Spinner size={16} /> : 'Create account'}
          </Button>
        </form>
        <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 12, textAlign: 'center' }}>
          Next: short onboarding, then your learning hub
        </p>
      </AuthShell>
    </>
  );
};

export default Signup;
